import { Button, ColumnLayout } from 'AWS-UI-Components-React';
import React from 'react';
import {
  ASIN_METRIC_TYPE,
  ASIN_TYPE,
  ASIN_TYPE_DROPDOWN_ELEMENTS,
  COMPONENT_NAMES,
  HISTOGRAM_DOWNLOAD_HEADINGS,
  HISTOGRAM_METRICS,
  METRIC_NAMES,
  HISTOGRAM_DROPDOWN_ELEMENTS,
} from '../../../constants/asin_metric_elements';
import { CHANNELS } from '../../../constants/channels';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from '../../../recharts';
import { AccuracyButtonNewStyle, ASINChartStyle } from '../../../styles/common_styles';
import { downloadFile } from '../../../utilities';
import { formatTimeFrameToYYYYMM } from '../../../utils/date-utils';
import { getMaximallyDifferentRandomColor } from '../../../utils/graph-utils';
import logger from '../../../utils/logging';
import { TrendFilter } from '../../buttons/Trend_Filter';
import { EmptyMessage } from '../../content/empty_message';
import { ErrorFlash } from '../../content/error_flash';
import { LoadingSpinner } from '../../content/loading_spinner';
import { getAsinMetricsClient } from './asin_metrics_client';
import {
  extractAsinType,
  extractComponent,
  extractMetric,
  filterLinesOrBars,
  generateCompositeKey,
  generateLabel,
  mergeKeysOfObjectsWithSameId,
} from './asin_metrics_utils';

export class HistogramBarChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingNetVarPerUnit: false,
      loadingAbsVarPerUnit: false,
      loadingWinsorizedNetVarPerUnit: false,
      loadingWinsorizedAbsVarPerUnit: false,
      loadError: false,
      selectedMetrics: [ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT],
      selectedComponents: ['contributionProfit', 'contributionProfitWithoutRefunds'],
      selectedAsinTypes: [ASIN_TYPE.ALL],
      downloadingHistogram: false,
    };
    this.histogramData = {};
    this.bucketData = {};
  };


  componentDidMount = () => {
    this.initialClientTasks();
  };

  initialClientTasks = () => {
    this.asinMetricsClient = getAsinMetricsClient();
    const histogramPayload = this.prepareHistogramPayload();
    HISTOGRAM_METRICS.forEach(metric => this.pullHistogramData(metric, histogramPayload));
  };

  updateLoading = (type, loading) => {
    switch (type) {
      case ASIN_METRIC_TYPE.NET_VAR_PER_UNIT:
        this.setState({loadingNetVarPerUnit: loading});
        break;
      case ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT:
        this.setState({loadingAbsVarPerUnit: loading});
        break;
      case ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT:
        this.setState({loadingWinsorizedNetVarPerUnit: loading});
        break;
      case ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT:
        this.setState({loadingWinsorizedAbsVarPerUnit: loading});
        break;
      default:
        logger.warn(`Attempting to update state for ${type} but was not found.`)
    }
  };

  getLoadDone = () => {
    return !this.state.loadingNetVarPerUnit && !this.state.loadingAbsVarPerUnit &&
      !this.state.loadingWinsorizedNetVarPerUnit && !this.state.loadingWinsorizedAbsVarPerUnit;
  }

  pullHistogramData = (type, payload) => {
    this.updateLoading(type, true);
    const newPayload = {...payload, metric: type};
    this.asinMetricsClient.getHistogram(this.processHistogramResults, this.onFailedRequest, newPayload);
  };

  prepareHistogramPayload = () => {
    let channel = this.props.payload.channel;
    if(channel === CHANNELS.RETAIL) channel = 'Retail'; //Backend only accepts Retail not RETAIL
    let payload = Object.assign({}, {
      channel, month: formatTimeFrameToYYYYMM(this.props.payload.timeFrame),
      gls: this.props.payload.gls, marketplaceId: this.props.payload.marketplace
    });
    if(this.props.payload.channelType){
      payload.channelType = parseInt(this.props.payload.channelType);
    }
    return payload;
  };

  processHistogramResults = (response, type) => {
    if(response && response.data){
      this.processHistogramBuckets(response.data, type);
      this.processHistogramValues(response.data, type);
    }
    this.updateLoading(type, false);
  };

  /* Convert histogramSnapshotByComponent to a list of histogram data
   Sample Input:
   { snapshot: 20200107,
   { "contributionProfit":[{"bucketId":"<-50","value":100},{"bucketId":"-50to-25","value":200}, ...],
   "displayAds":[{"bucketId":"<-50","value":100},{"bucketId":"-50to-25","value":200},...]},... }
   }
   Sample Output:
   [{bucketId: "<-50", 20200107-NetLAPerUnit-Contribution Profit: 100, 20200107-NetLAPerUnit-Display Ads: 100, ...},
   {bucketId: "-50to-25", 20200107-NetLAPerUnit-Contribution Profit: 200, 20200107-NetLAPerUnit-Display Ads: 200, ...}, ...
   ]
   */
  processHistogramValues = (data, type) => {
    if(data.histogramSnapshots && data.histogramSnapshots.length && data.histogramSnapshots.length > 0){

      let histogramData = [];
      let components = new Set();
      let barKeys = new Set();

      for(const histogramSnapshot of data.histogramSnapshots){
        const {histogramSnapshotByComponent, snapshot, asinType} = histogramSnapshot;
        const metric = type;
        /* key: bucketId, value: valueMap
         bucketId => Map(compositeKey(snapshotDate-metric-component) => value)
         */
        let bucketMap = new Map();
        for (const componentKey of Object.keys(histogramSnapshotByComponent)){
          const buckets = histogramSnapshotByComponent[componentKey];
          const component = componentKey;
          components.add(component);
          const compositeKey = generateCompositeKey(snapshot, metric, component, asinType);
          for ( const bucket of buckets) {
            if (!bucketMap.has(bucket.bucketId)) {
              bucketMap.set(bucket.bucketId, new Map())
            }
            let valueMap = bucketMap.get(bucket.bucketId);
            if(!valueMap.has(compositeKey)){
              valueMap.set(compositeKey, bucket.value);
            }
          }
        }

        for (const [bucketId, valueMap] of bucketMap.entries()){
          let item = {};
          item[`bucketId`] = bucketId;
          for(const [key, value] of valueMap.entries()) {
            item[key] = value;
            barKeys.add(key);
          }
          histogramData.push(item);
        }
      }
      this.histogramData[type] = {};
      this.histogramData[type].data = histogramData;
      this.histogramData[type].bars = [...barKeys];
      this.histogramData[type].components = [...components];
    }
  };

  processHistogramBuckets = (data, type) => {
    if(data.bucketInfoList){
      const bucketData = data.bucketInfoList;
      let buckets = {};
      bucketData.forEach(b => buckets[b.id] = b.description);
      this.bucketData[type] = buckets;
    }
  };

  getBars = (selectedComponents, selectedMetrics, selectedAsinTypes) => {
    const ids = this.getHistogramBarIds(selectedComponents, selectedMetrics, selectedAsinTypes);
    let bars = [];
    let colorIndex = 0;
    ids.forEach((id) => bars.push(<Bar dataKey={id}
                                       key={id}
                                       name={generateLabel(id, false,
                                                           selectedMetrics.length > 1,
                                                           selectedAsinTypes.length > 1)}
                                       onClick={this.onBarClick.bind(this, id)}
                                       fill={getMaximallyDifferentRandomColor(colorIndex++, ids.length)}/>));
    return bars;
  };

  onBarClick = (barId, data) => {
    const bucketId = data.payload.bucketId;
    const component = extractComponent(barId);
    const metric = extractMetric(barId);
    const asinType = extractAsinType(barId);
    this.props.pullAsinInfo(metric, bucketId, component, asinType);
  };

  /*
   Filter histogram bars based on selected components, metrics, and asinTypes
   */
  getHistogramBarIds = (selectedComponents, selectedMetrics, selectedAsinTypes) => {
    let combinedBars = HISTOGRAM_METRICS.filter(type => this.histogramData && this.histogramData[type] && this.histogramData[type].bars)
                                        .map(type => this.histogramData[type].bars)
                                        .flat();
    combinedBars = filterLinesOrBars(combinedBars, selectedComponents, selectedMetrics, null, selectedAsinTypes);
    return combinedBars;
  };

  getComponentDropDownElements = () => {
    let combinedComponents = HISTOGRAM_METRICS.filter(type => this.histogramData && this.histogramData[type] && this.histogramData[type].components)
                                              .map(type => this.histogramData[type].components);
    let uniqueComponents = [...new Set(combinedComponents.flat())];
    return uniqueComponents.map(comp => {return {value: comp, label: COMPONENT_NAMES[comp]}; });
  };

  setSelectedMetrics = (chosen) => {
    const ids = chosen.detail.selectedOptions.map(o => o.value);
    this.setState({selectedMetrics: ids})
  };

  setSelectedComponents = (chosen) => {
    const ids = chosen.detail.selectedOptions.map(o => o.value);
    this.setState({selectedComponents: ids});
  };

  setSelectedAsinTypes = (chosen) => {
    const ids = chosen.detail.selectedOptions.map(o => o.value);
    this.setState({selectedAsinTypes: ids})
  };

  onFailedRequest = () => {
    this.setState({loadError: true});
  };

  getHistogramData = () => {
    let combinedData = HISTOGRAM_METRICS.filter(type => this.histogramData && this.histogramData[type] && this.histogramData[type].data)
                                        .map(type => this.histogramData[type].data)
                                        .flat();
    /*
     Merge keys of histogram rows with the same bucketId
     Sample Input:
     [{bucketId: "<-50", 20200107-NetLAPerUnit-Contribution Profit: 100, 20200107-NetLAPerUnit-Display Ads: 100},
     {bucketId: "<-50", 20200107-NetFAPerUnit-Contribution Profit: 100, 20200107-NetFAPerUnit-Display Ads: 100}]
     Sample Output:
     [{bucketId: "<-50", 20200107-NetLAPerUnit-Contribution Profit: 100, 20200107-NetLAPerUnit-Display Ads: 100,
     20200107-NetFAPerUnit-Contribution Profit: 100, 20200107-NetFAPerUnit-Display Ads: 100}]
     */
    return mergeKeysOfObjectsWithSameId(combinedData, 'bucketId');
  };

  onDownloadData = (histogramName) => {
    this.setState({downloadingHistogram: true})
    const histogramData = this.histogramData || {};
    let combinedData = !histogramData ? [] : HISTOGRAM_METRICS.filter(type => histogramData[type] && histogramData[type].data)
                                                    .map(type => histogramData[type].data)
                                                    .flat();
    const {channel, marketplace, productLines, timeFrame} = this.props.payload;
    let downloadDataArr = [HISTOGRAM_DOWNLOAD_HEADINGS];
    combinedData.forEach(row => {
      const bucketId = row.bucketId;
      for(const key of Object.keys(row)){
        if(key !== 'bucketId'){
          const metric = extractMetric(key);
          const metricName = METRIC_NAMES[metric];
          const component = COMPONENT_NAMES[extractComponent(key)];
          const asinCount = row[key];
          const asinType = extractAsinType(key);
          const bucket = this.bucketData[metric] && this.bucketData[metric][bucketId];
          downloadDataArr.push([marketplace, channel, timeFrame, productLines, component, metricName, bucket, asinCount, asinType]);
        }
      }
    });
    const downloadData = downloadDataArr.map(rowArr => rowArr.join('\t')).join('\n');
    downloadFile(downloadData, `${histogramName} Data.xls`);
    this.setState({downloadingHistogram: false});
  };

  formatXAxis = (tickItem) => {
    return tickItem.substring(2);
  }

  render() {
    const {histogramName} = this.props;
    const {selectedMetrics, selectedComponents, selectedAsinTypes} = this.state;
    const componentOpts = this.getComponentDropDownElements();
    const histogramData = this.getHistogramData();
    const histogramDataEmpty = !histogramData || !histogramData.length;

    return (
      <div className={ASINChartStyle}>
        <div className="awsui-util-container" >
          <div className="awsui-util-container-header">
            <div className="awsui-util-action-stripe">
              <div className="awsui-util-action-stripe-title">
                <div className="awsui-util-font-size-2">
                  {histogramName}
                </div>
              </div>
              <Button
                text="Download"
                variant="normal"
                disabled={!this.getLoadDone() || histogramDataEmpty}
                loading={this.state.downloadingHistogram}
                onClick={this.onDownloadData.bind(this, histogramName)}
                data-cy="report-download-button"
              />
            </div>
          </div>
          {this.getLoadDone() && !histogramDataEmpty &&
          <div>
            <ColumnLayout columns={3}>
              <div data-awsui-column-layout-root="true">
                <div>
                  <TrendFilter
                    text="Component"
                    classname={AccuracyButtonNewStyle}
                    options={componentOpts}
                    onChange={this.setSelectedComponents}
                    selectedIds={selectedComponents}
                  />
                </div>
                <div>
                  <TrendFilter
                    text="ASIN Metric"
                    classname='AccuracyTypeMultiSelect'
                    options={HISTOGRAM_DROPDOWN_ELEMENTS}
                    onChange={this.setSelectedMetrics}
                    selectedIds={selectedMetrics}
                  />
                </div>
                <div>
                  <TrendFilter
                    text="ASIN Type"
                    classname='AccuracyTypeMultiSelect'
                    options={ASIN_TYPE_DROPDOWN_ELEMENTS}
                    onChange={this.setSelectedAsinTypes}
                    selectedIds={selectedAsinTypes}
                  />
                </div>
              </div>
            </ColumnLayout>
            <div style={{width:'99%', padding:'0'}}>
              <ResponsiveContainer aspect={2.5}>
                <BarChart
                  data={ histogramData }
                  margin={ {top: 10, right: 30, left: 20, bottom: 10,} }
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bucketId" tickFormatter={this.formatXAxis}
                         label={{value: 'Per Unit Variance Buckets', position: 'insideBottomLeft', dx: -10, dy: 10}}
                  />
                  <YAxis label={{value: 'ASIN Count (Millions)', angle: -90, position: 'insideLeft', dy: 30}}
                         tickFormatter={(val)=> val/1000000}/>
                  <Tooltip />
                  <Legend margin={{ top: 0, left: 0, right: 0, bottom: 0 } }/>
                  {this.getBars(selectedComponents, selectedMetrics, selectedAsinTypes)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          }
          {!this.getLoadDone() && !this.state.loadError && <LoadingSpinner />}
          {this.getLoadDone() && histogramDataEmpty && <EmptyMessage />}
          {this.state.loadError && (<ErrorFlash type={histogramName} />)}
        </div>
      </div>);
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/asin_metrics/histogram_bar_chart.jsx